import React, { useState, useEffect, useRef } from "react"
import { ComposableMap, Geographies, Geography } from "react-simple-maps"
import { geoMiller } from "d3-geo-projection"
import LendingMapPatterns from "./lending-map-patterns"
import findObject from "../helpers/find-object"
import { roundNumber } from "../helpers/numbers"
import IconExternalLink from "../components/svg/icon-external-link"
import TextField from "@material-ui/core/TextField"
import Autocomplete from "@material-ui/lab/Autocomplete"
import Select from "@material-ui/core/Select"
import InputLabel from "@material-ui/core/InputLabel"
import MenuItem from "@material-ui/core/MenuItem"
import FormControl from "@material-ui/core/FormControl"
import Modal from "react-modal"
import { useWindowSize } from "../hooks/useWindowSize"

// Data imports
import geography from "../data/world-110m-custom-shapes.json"
import lakes from "../data/world-110m-lakes.json"
import lendingMapData from "../data/lending-map-data.json" // TODO: Consider components accessing this directly instead of passing a prop
import lendingMapAgreements from "../data/lending-map-agreements.json"

// MAP NOTES:
// - File: '../data/world-110m-custom-shapes.json'
// - Source: https://international.ipums.org/international/gis.shtml
// - Converted to TopoJSON with https://mapshaper.org/
//     Simplification settings: 0.40%, use planar geometry, Visvalingam/weighted area method
// - Merged with data of '../data/raw/world-110m-raw.json' from React Simple Maps
// - Data modified to match IMF data/taxonomies

// Create the lending map
function LendingMap() {
  const mapInnerWrapperRef = useRef()
  const [agreementsFilter, setAgreementsFilter] = useState("") // Accepts an agreement abbreviation
  const [countriesFilter, setCountriesFilter] = useState("") // Accepts a country iso_a2
  const [tooltipActive, setTooltipActive] = useState(false)
  const [tooltipCountry, setTooltipCountry] = useState("") // Accepts a country iso_a2
  const [tooltipPosition, setTooltipPosition] = useState({
    x: -999,
    y: -999,
    positionLeft: false,
  }) // Position off screen as a safeguard
  const [overlayActive, setOverlayActive] = useState(false)
  const [overlayAgreement, setOverlayAgreement] = useState("") // Accepts an agreement abbreviation
  const [overlayAgreementsHeight, setOverlayAgreementsHeight] = useState(0)
  const windowSize = useWindowSize()

  // Setup the projection
  const width = 800
  const height = 450

  const customProjection = geoMiller()
    .translate([width / 2, height / 2])
    .rotate([-10, 0, 0])

  // Process all actively-filtered countries
  var filteredData = agreementsFilter
    ? filterDataByAgreement(lendingMapData, agreementsFilter)
    : lendingMapData

  if (countriesFilter) {
    filteredData = filteredData.filter(
      country => country.iso_a2 === countriesFilter
    )
  }

  //
  // Toggle functions
  //

  function toggleTooltip(event, country) {
    if (event || country) {
      let rect = mapInnerWrapperRef.current.getBoundingClientRect()
      let nullShapes = [
        { id: "SC", x: 0.71, y: 0.69 },
        { id: "VC", x: 0.425, y: 0.57 },
        { id: "TO", x: 1, y: 0.77 },
      ]
      let id
      let countryRect
      let inAsiaOceania

      if (event) {
        id = event.currentTarget.id
        countryRect = event.currentTarget.getBoundingClientRect()
        inAsiaOceania = event.currentTarget
          .getAttribute("data-continent")
          .match(/^(Asia|Oceania)$/) // Must use getAttribute() for IE11
      } else if (country) {
        id = country.iso_a2
        countryRect = document
          .querySelector(`#${country.iso_a2}`)
          .getBoundingClientRect()
        inAsiaOceania = country.continent.match(/^(Asia|Oceania)$/)
      }

      // If the id matches a nullShape, we use that to conditionally provide x and y props that will override the default
      // Otherwise countryRect will return values that aren't correct
      // This ensures tooltips for the nullShape countries are positioned correctly
      let nullShape = findObject(nullShapes, "id", id)

      if (nullShape) {
        var x = nullShape.x * rect.width
        var y = nullShape.y * rect.height
      }

      setTooltipCountry(id)

      // Set coordinates as relative to the center-left or center-right position of the target country shape
      setTooltipPosition({
        x: x
          ? x
          : (inAsiaOceania ? countryRect.left : countryRect.right) - rect.left,
        y: y ? y : countryRect.top + countryRect.height / 2 - rect.top,
        positionLeft: inAsiaOceania,
      })

      setTooltipActive(true)
    } else {
      setTooltipActive(false)
    }
  }

  function toggleMapOverlay(agreementAbbreviation) {
    if (agreementAbbreviation) {
      setOverlayActive(true)
      setOverlayAgreement(agreementAbbreviation)

      let overlay = document.getElementsByClassName("ReactModal__Overlay") // Can't use refs as they break inside ReactModals
      if (overlay.length) {
        overlay[0].scrollTo({
          top: 0,
          left: 0,
          behavior: "smooth",
        })
      }

      // Impose a delay on handleResize so that initial modal open allows overlay to mount first
      setTimeout(() => {
        handleResize()
      }, 100)
    } else {
      setOverlayActive(false)
      setOverlayAgreement("")
    }
  }

  //
  // Event Handlers
  //

  // Close overlay on escape key hit
  function handleKeydown(event) {
    if (overlayActive && event.keyCode === 27) {
      toggleMapOverlay()
    }
  }

  function handleResize() {
    let overlayAgreements = document.querySelector(
      ".lending-map__overlay__agreements-wrapper"
    ) // Can't use refs as they break inside ReactModals
    if (overlayAgreements) {
      setOverlayAgreementsHeight(
        overlayAgreements.getBoundingClientRect().height
      )
    }
  }

  //
  // Effects
  //

  useEffect(() => {
    if (filteredData.length === 1) {
      toggleTooltip(null, filteredData[0])
    } else {
      toggleTooltip()
    }
  }, [agreementsFilter, countriesFilter])

  useEffect(() => {
    document.addEventListener("keydown", handleKeydown)
    return () => {
      document.removeEventListener("keydown", handleKeydown)
    }
  })

  useEffect(() => {
    handleResize() // Initial call

    window.addEventListener("resize", handleResize)
    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [])

  return (
    <section
      id="lending-map"
      className={
        overlayActive
          ? "lending-map lending-map--overlay-is-active"
          : "lending-map"
      }
    >
      <header>
        <h2 className="title title--xs lg:title--s title--light title--uppercase">
          Lending Map
        </h2>
        <p>
          <span className="title title--l md:title--xl title--bold" style={{marginTop: 5, marginBottom: 5 }}>
            Financial Assistance Approved in FY 2021
          </span>{" "}
          As of April 30, 2021 (in millions of special drawing rights, M SDR)
        </p>
      </header>

      <div className="lending-map__map-outer-wrapper">
        <div
          className="lending-map__map-inner-wrapper"
          ref={mapInnerWrapperRef}
        >
          <LendingMapFilterBar
            filteredData={filteredData}
            agreementsFilter={agreementsFilter}
            setAgreementsFilter={setAgreementsFilter}
            setCountriesFilter={setCountriesFilter}
            toggleTooltip={toggleTooltip}
            windowSize={windowSize}
          />

          <LendingMapPatterns />

          <div className="lending-map__map__map-wrapper">
            <ComposableMap
              className="lending-map__map"
              projection={customProjection}
              height={350}
            >
              <Geographies
                className="lending-map__map__geography"
                geography={geography}
              >
                {({ geographies }) => {
                  return geographies
                    .filter(geo => geo.properties.CONTINENT !== "Antarctica")
                    .map((geo, index) => {
                      const { ISO_A2 } = geo.properties

                      // Identify each country in the data that matches a geo
                      let country = findObject(lendingMapData, "iso_a2", ISO_A2)

                      // Mark the country as active if it matches the filteredData
                      let activeCountry =
                        country &&
                        findObject(filteredData, "iso_a2", country.iso_a2)

                      // Return a grey base shape for all countries
                      // Plus, return an additional filled shape for all countries that match the unfiltered data
                      // States and filtered data control which filled shapes are visible or not, via className. See lending-map.scss
                      return (
                        <g key={geo.rsmKey}>
                          <Geography
                            key={`base-${geo.rsmKey}-${index}`}
                            id={`base-${ISO_A2}-${index}`}
                            geography={geo}
                            fill="#dadee9"
                            tabIndex="-1"
                          />
                          {country && (
                            <Geography
                              key={`fill-${geo.rsmKey}-${index}`}
                              id={ISO_A2}
                              className={
                                activeCountry ? "fill" : "fill fill--inactive"
                              }
                              geography={geo}
                              fill={getFill(country)}
                              tabIndex="-1"
                              onMouseEnter={event => toggleTooltip(event)}
                              onMouseLeave={() => toggleTooltip()}
                              aria-haspopup={activeCountry ? "true" : null}
                              aria-controls={
                                activeCountry ? "lending-map__tooltip" : null
                              }
                              data-continent={country.continent}
                            />
                          )}
                        </g>
                      )
                    })
                }}
              </Geographies>

              <Geographies
                className="lending-map__map__lakes"
                geography={lakes}
              >
                {({ geographies }) => {
                  return geographies.map((geo, index) => {
                    return (
                      <Geography
                        key={`lake-${geo.rsmKey}-${index}`}
                        geography={geo}
                        tabIndex="-1"
                      />
                    )
                  })
                }}
              </Geographies>
            </ComposableMap>
          </div>
        </div>

        <LendingMapAgreementsList
          data={lendingMapData}
          toggleMapOverlay={toggleMapOverlay}
        />

        <LendingMapTooltip
          data={lendingMapData}
          tooltipActive={tooltipActive}
          tooltipCountry={tooltipCountry}
          tooltipPosition={tooltipPosition}
        />
      </div>

      <footer>
        <section className="lending-map__footer__exchange-rates">
          <header className="title title--xxs lg:title--xs">
            SDR/USD Exchange Rate
          </header>
          <p className="title title--xxs lg:title--xs">
            April 30, 2021: SDR 1 = US$1.43599
          </p>
        </section>
        <section className="lending-map__footer__meta">
          <p className="title title--xxs lg:title--xs">Aug. = Augmentation</p>
          <p className="title title--xxs lg:title--xs">
            Country borders do not necessarily reflect the IMF's official
            position.
          </p>
          <p className="title title--xxs lg:title--xs">
            Lending map as of April 30, 2021. Visit{" "}
            <a href="https://www.imf.org">imf.org</a> for the latest
            information.
          </p>
        </section>
      </footer>

      <LendingMapOverlay
        data={lendingMapData}
        toggleMapOverlay={toggleMapOverlay}
        overlayActive={overlayActive}
        overlayAgreement={overlayAgreement}
        overlayAgreementsHeight={overlayAgreementsHeight}
        windowSize={windowSize}
      />
    </section>
  )
}

//
// Subcomponents
//
function LendingMapFilterBar({
  filteredData,
  agreementsFilter,
  setAgreementsFilter,
  setCountriesFilter,
  toggleTooltip,
  windowSize,
}) {
  return (
    <div id="lending-map__filter-bar" className="lending-map__filter-bar">
      <div id="lending-map__filters" className="lending-map__filters">
        <div className="lending-map__autocomplete-filter">
          {windowSize.width < 768 ? (
            <FormControl variant="outlined">
              <InputLabel id="agreements-select-filter-label">
                Agreement Type
              </InputLabel>
              <Select
                name="agreements-select-filter"
                id="agreements-select-filter"
                labelId="agreements-select-filter-label"
                label="Agreement Type"
                value={agreementsFilter}
                aria-label="Filter the lending map agreements"
                aria-controls="lending-map"
                onChange={event => {
                  setAgreementsFilter(event.target.value)
                }}
              >
                <MenuItem value="">
                  <em>All</em>
                </MenuItem>
                {lendingMapAgreements.map(option => (
                  <MenuItem
                    key={option.abbreviation}
                    value={option.abbreviation}
                  >
                    {option.type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Autocomplete
              name="agreements-autocomplete-filter"
              id="agreements-autocomplete-filter"
              size="small"
              disablePortal={true}
              value={
                agreementsFilter
                  ? lendingMapAgreements.find(
                      option => option.abbreviation === agreementsFilter
                    )
                  : null
              }
              options={lendingMapAgreements}
              getOptionLabel={option => option.type}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Agreement Type"
                  variant="outlined"
                />
              )}
              aria-label="Filter the lending map agreements"
              aria-controls="lending-map"
              onChange={(event, value, reason) => {
                setAgreementsFilter(value && value.abbreviation)
              }}
            />
          )}
        </div>

        <div className="lending-map__autocomplete-filter">
          <Autocomplete
            name="countries-autocomplete-filter"
            id="countries-autocomplete-filter"
            size={windowSize.width < 768 ? "medium" : "small"}
            disablePortal={true}
            options={lendingMapData}
            getOptionLabel={option => option.name}
            renderInput={params => (
              <TextField {...params} label="Country" variant="outlined" />
            )}
            aria-label="Filter the lending map countries"
            aria-controls="lending-map"
            onChange={(event, value, reason) => {
              setCountriesFilter(value && value.iso_a2)
            }}
          />
        </div>
      </div>

      <div
        className={
          filteredData.length === 0
            ? "lending-map__filter-bar__countries-list lending-map__filter-bar__countries-list--none"
            : "lending-map__filter-bar__countries-list"
        }
      >
        {filteredData.length === 0 ? (
          <>
            <p className="title title--xs md:title--s">
              No countries match the filter combination.
            </p>
            <button
              type="button"
              className="title title--xs md:title--s"
              aria-controls="lending-map"
              onClick={() => setAgreementsFilter()}
            >
              Clear Agreement Type filter to see results
            </button>
          </>
        ) : (
          <ul>
            {filteredData.map((country, index) => {
              return (
                <li
                  key={`filter-bar-countries-list-item-${country.iso_a2}-${index}`}
                  className="lending-map__filter-bar__countries-list__item"
                  aria-controls="lending-map__tooltip"
                  onMouseEnter={() => toggleTooltip(null, country)}
                  onMouseLeave={() => toggleTooltip()}
                  onFocus={() => toggleTooltip(null, country)}
                  onBlur={() => toggleTooltip()}
                  tabIndex="0"
                >
                  <header className="title title--xs md:title--s title--bold">
                    {country.name}
                  </header>
                  <ul>
                    {country.agreements.map((agreement, index) => {
                      return (
                        <li
                          key={`filter-bar-countries-list-item-${country.iso_a2}-${agreement.abbreviation}-${index}`}
                          className="title title--xxs md:title--xs"
                        >
                          <LendingMapAgreementDot
                            color={
                              findObject(
                                lendingMapAgreements,
                                "abbreviation",
                                agreement.abbreviation
                              ).color.hex
                            }
                          />
                          <span className="util__screen-reader-only">
                            {agreement.type}
                          </span>
                          {agreement.amount === 0
                            ? "* (See Agreement Overlay)"
                            : `${roundNumber(
                                agreement.amount,
                                "millions",
                                "all"
                              )}M
                             SDR`}
                          {agreement.augmented &&
                            ` (Aug. of ${agreement.abbreviation})`}
                        </li>
                      )
                    })}
                  </ul>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}

// Creates the lending map tooltip, containing the information of the country passed to it
function LendingMapTooltip({
  data,
  tooltipCountry,
  tooltipPosition,
  tooltipActive,
}) {
  let country = findObject(data, "iso_a2", tooltipCountry)

  return (
    <div
      id="lending-map__tooltip"
      className={
        tooltipPosition.positionLeft
          ? "lending-map__tooltip lending-map__tooltip--left"
          : "lending-map__tooltip"
      }
      data-active={tooltipActive}
      style={{
        top: tooltipPosition.y,
        left: tooltipPosition.x,
      }}
    >
      {country && (
        <div>
          <header className="title title--r md:title--m title--bold">
            {country.name}
          </header>
          <ul>
            {country.agreements.map((countryAgreement, index) => {
              let agreement = findObject(
                lendingMapAgreements,
                "abbreviation",
                countryAgreement.abbreviation
              )

              return (
                <li
                  key={`tooltip-agreement-${country.iso_a2}-${countryAgreement.abbreviation}-${countryAgreement.amount}-${index}`}
                >
                  <header className="title title--xs md:title--s title--bold">
                    {countryAgreement.amount === 0
                      ? "* (See Agreement Overlay)"
                      : `${roundNumber(
                          countryAgreement.amount,
                          "millions",
                          "all"
                        )}M SDR`}
                  </header>
                  <p className="title title--xxs md:title--xs title--bold">
                    <LendingMapAgreementDot color={agreement.color.hex} />
                    {countryAgreement.augmented && "Aug. of "}
                    {countryAgreement.type}
                  </p>
                  <p className="title title--xxs">
                    Approved on {countryAgreement.approval_date}
                  </p>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}

// Creates the lending map agreements list with numbers for the data passed to it
function LendingMapAgreementsList({
  data,
  toggleMapOverlay,
  overlayAgreement,
}) {
  return (
    <section className="lending-map__agreements-list">
      <h3 className="title title--xxs title--light">
        Click on a Lending Agreement to See More Info
      </h3>
      <div className="lending-map__agreements-list__agreements">
        {lendingMapAgreements.map((agreement, index) => {
          let agreementTotal = roundNumber(
            getAgreementTotal(data, agreement.abbreviation),
            "millions"
          )

          return (
            <button
              key={`agreements-button-${agreement.abbreviation}`}
              className={
                agreement.abbreviation === overlayAgreement
                  ? `lending-map__agreements-list__agreements__button lending-map__agreements-list__agreements__button--active lending-map__agreements-list__agreements__button--${agreement.color.name}`
                  : `lending-map__agreements-list__agreements__button lending-map__agreements-list__agreements__button--${agreement.color.name}`
              }
              style={{ borderColor: agreement.color.hex }}
              type="button"
              aria-haspopup="true"
              aria-label={`Open details overlay for ${agreement.type}`}
              aria-controls="lending-map__overlay"
              onClick={event => {
                toggleMapOverlay(agreement.abbreviation)
              }}
            >
              <header className="title title--xxs md:title--m title--bold">
                {agreementTotal}
                <span className="title--xxs title--light"> Million SDR</span>
              </header>
              <p className="title title--xxs title--bold">
                <LendingMapAgreementDot color={agreement.color.hex} />
                {agreement.type}
              </p>
            </button>
          )
        })}
      </div>
    </section>
  )
}

// Creates the lending map overlay, containing the information of the agreement passed to it
function LendingMapOverlay({
  data,
  toggleMapOverlay,
  overlayActive,
  overlayAgreement,
  overlayAgreementsHeight,
  windowSize,
}) {
  let agreement = findObject(
    lendingMapAgreements,
    "abbreviation",
    overlayAgreement
  )

  return (
    <Modal
      id="lending-map__overlay"
      isOpen={overlayActive}
      overlayClassName="lending-map__overlay"
      className="lending-map__overlay__modal-inner"
      bodyOpenClassName="util__body-lock"
    >
      {agreement && (
        <div
          className="lending-map__overlay__content-wrapper"
          style={{ marginBottom: overlayAgreementsHeight }}
        >
          <header className="lending-map__overlay__header">
            <div>
              <h3 className="title title--m md:title--l">
                <LendingMapAgreementDot
                  color={agreement.color.hex}
                  spacingLarge={true}
                />
                {agreement.type}
                <span className="title title--s">
                  ({agreement.abbreviation})
                </span>
              </h3>
              <p>
                {agreement.adopted !== ""
                  ? `(adopted in ${agreement.adopted})`
                  : null}
              </p>
              <p className="title title--r md:title--m title--bold">
                {roundNumber(
                  getAgreementTotal(data, agreement.abbreviation),
                  "millions"
                )}
                <span className="title--xs md:title--s"> Milllion SDR</span>
              </p>
            </div>
            <button
              className="lending-map__overlay__close-button"
              type="button"
              aria-label="Close map overlay"
              aria-controls="site-mobile-nav"
              onClick={() => toggleMapOverlay()}
            ></button>
          </header>
          <div className="lending-map__overlay__content">
            <div className="lending-map__overlay__content-column">
              {agreement.details.length === 1 ? (
                <LendingMapOverlaySection
                  key={`overlay-${agreement.abbreviation}-${agreement.details[0].title}`}
                  title={agreement.details[0].title}
                  content={agreement.details[0].content}
                />
              ) : (
                <div className="lending-map__overlay__content-column__inner-columns">
                  {agreement.details.map(detail => (
                    <LendingMapOverlaySection
                      key={`overlay-${agreement.abbreviation}-${detail.title}`}
                      title={detail.title}
                      content={detail.content}
                    />
                  ))}
                </div>
              )}
              {agreement.footnotes && (
                <div
                  className="lending-map__overlay__content__footnotes title--xxs md:title--xs util__desktop-only"
                  dangerouslySetInnerHTML={{ __html: agreement.footnotes }}
                />
              )}
            </div>
            <div className="lending-map__overlay__content-column">
              <LendingMapOverlaySectionTable
                title="Distribution of Funds"
                data={data}
                agreementAbbreviation={agreement.abbreviation}
              />
              {agreement.footnotes && (
                <div
                  className="lending-map__overlay__content__footnotes title--xxs md:title--xs util__mobile-only"
                  dangerouslySetInnerHTML={{ __html: agreement.footnotes }}
                />
              )}
            </div>
          </div>
        </div>
      )}

      <div className="lending-map__overlay__agreements-wrapper">
        {windowSize.width < 1440 ? (
          <FormControl variant="outlined">
            <InputLabel id="agreements-overlay-select-filter-label">
              Current Lending Agreement
            </InputLabel>
            <Select
              name="agreements-overlay-select-filter"
              id="agreements-overlay-select-filter"
              labelId="agreements-overlay-select-filter-label"
              label="Current Lending Agreement"
              value={overlayAgreement}
              aria-label="Filter the lending map agreements"
              aria-controls="lending-map"
              onChange={event => {
                toggleMapOverlay(event.target.value)
              }}
            >
              {lendingMapAgreements.map(option => (
                <MenuItem key={option.abbreviation} value={option.abbreviation}>
                  {option.type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <LendingMapAgreementsList
            data={lendingMapData}
            toggleMapOverlay={toggleMapOverlay}
            overlayAgreement={overlayAgreement}
          />
        )}
      </div>
    </Modal>
  )
}

// Overlay subcomponent
function LendingMapOverlaySection({ title, content }) {
  return (
    <div className="lending-map__overlay__content__section">
      <h4 className="title title--s md:title--r">{title}</h4>
      <div dangerouslySetInnerHTML={{ __html: content }} />
    </div>
  )
}

// Overlay subcomponent
function LendingMapOverlaySectionTable({ title, data, agreementAbbreviation }) {
  let countries = filterDataByAgreement(data, agreementAbbreviation)

  return (
    <div className="lending-map__overlay__content__section">
      <h4 className="title-s md:title--r">{title}</h4>
      <table>
        <thead>
          <tr>
            <th className="title title--xxs md:title--xs">Country</th>
            <th className="title title--xxs md:title--xs">Date of Approval</th>
            <th className="title title--xxs md:title--xs">Amount (SDR)</th>
            <th className="title title--xxs md:title--xs">Press Release</th>
          </tr>
        </thead>
        <tbody>
          {countries &&
            countries.map(country => {
              return (
                country.agreements &&
                country.agreements.map((agreement, index) => {
                  let dateTimeArray = agreement.approval_date.split("/")

                  // Mutate the dateTimeArray to dateTime order
                  dateTimeArray.unshift(dateTimeArray.pop())

                  // Create and store valid dateTimeString
                  let dateTimeString = dateTimeArray.join("-")

                  return (
                    <tr
                      key={`overlay-table-row-${agreementAbbreviation}-${country.iso_a2}-${index}`}
                      className={
                        "lending-map__overlay__table-row" +
                        (index > 0
                          ? " lending-map__overlay__table-row--secondary"
                          : "")
                      }
                    >
                      <td className="title title--xxs md:title--xs">
                        {index > 0 ? (
                          <span className="util__screen-reader-only">
                            {country.name}
                          </span>
                        ) : (
                          country.name
                        )}
                      </td>
                      <td className="title title--xxs md:title--xs">
                        <time dateTime={dateTimeString}>
                          {agreement.approval_date}
                        </time>
                      </td>
                      <td className="title title--xxs md:title--xs">
                        {agreement.amount === 0 ? (
                          <data value={0}>*</data>
                        ) : (
                          <data value={agreement.amount}>
                            {roundNumber(agreement.amount, "millions", "all")}
                            <span> million</span>
                            {agreement.augmented && (
                              <span className="lending-map__overlay__table-row__augmented-dagger">
                                <sup>†</sup>
                              </span>
                            )}
                          </data>
                        )}
                      </td>
                      <td className="title title--xxs md:title--xs">
                        <a
                          className="lending-map__overlay__table-row__press-release-link"
                          href={agreement.link}
                          target="_blank"
                          rel="noreferrer"
                        >
                          View <IconExternalLink />
                        </a>
                      </td>
                    </tr>
                  )
                })
              )
            })}
        </tbody>
      </table>
    </div>
  )
}

function LendingMapAgreementDot({ color, spacingLarge }) {
  return (
    <i
      className={
        spacingLarge
          ? "lending-map__agreement-dot lending-map__agreement-dot--spacing-large"
          : "lending-map__agreement-dot"
      }
      style={{ backgroundColor: color }}
      role="presentation"
    />
  )
}

//
// Utilities
//

// Returns an object containing arrays of the agreement color names and color values that match the given dataset
function getColors(dataset) {
  // Initialize arrays
  let colorNames = [],
    colorValues = []

  dataset.agreements.forEach(datasetAgreement => {
    let agreement = findObject(
      lendingMapAgreements,
      "abbreviation",
      datasetAgreement.abbreviation
    )

    if (!colorNames.includes(agreement.color.name)) {
      colorNames.push(agreement.color.name) // Only push novel color names
    }

    if (!colorValues.includes(agreement.color.hex)) {
      colorValues.push(agreement.color.hex) // Only push novel color values
    }
  })

  return {
    colorNames,
    colorValues,
  }
}

// Returns the default fill, agreement fill, or agreement fill pattern string to be used as the fill for a country
function getFill(country) {
  // Get the color names and values that apply to the country
  var { colorNames, colorValues } = getColors(country)

  // If there is more than one fill color, pass a predictable URL string for the fill, then generate a <linearGradient> to inject into the svg <defs>
  if (colorNames.length > 1 && colorValues.length > 1) {
    let fillPatternString = `pattern-stripe-${colorNames.sort().join("-")}`

    return `url('#${fillPatternString}')`
  }

  // If there is only one fill color, pass its value
  return colorValues[0]
}

// // Returns an array of only the countries with agreements (and only the agreements therein) that match the filter
// // H/T https://stackoverflow.com/a/42167998
// function filterDataByFilter(dataset, filter) {
//   return dataset.reduce(function (acc, country) {
//     const agreements = country.agreements.filter(
//       agreement => agreement[filter] === true
//     )
//     return !agreements.length
//       ? acc
//       : acc.concat(
//           Object.assign({}, country, {
//             agreements,
//           })
//         )
//   }, [])
// }

// Modified version of the above function to be consolidated later
// Returns an array of only the countries with agreements (and only the agreements therein) that match the agreement type
function filterDataByAgreement(dataset, agreementAbbreviation) {
  return dataset.reduce(function (acc, country) {
    const agreements = country.agreements.filter(
      agreement => agreement.abbreviation === agreementAbbreviation
    )
    return !agreements.length
      ? acc
      : acc.concat(
          Object.assign({}, country, {
            agreements,
          })
        )
  }, [])
}

// Returns the sum of all agreements of a given type across all countries of a dataset
function getAgreementTotal(dataset, agreementAbbreviation) {
  let agreementTotal = 0 // Initialize value to avoid NaN errors

  dataset.forEach(item => {
    item.agreements.forEach(agreement => {
      if (agreementAbbreviation === agreement.abbreviation) {
        agreementTotal += parseInt(agreement.amount)
      }
    })
  })

  return agreementTotal
}

// // Returns the sum of all agreements across all countries of a dataset
// function getAgreementsGrandTotal(dataset) {
//   let agreementsGrandTotal = 0 // Initialize the total at zero to avoid NaN errors
//
//   dataset.forEach(item => {
//     item.agreements.forEach(agreement => {
//       agreementsGrandTotal += parseInt(agreement.amount)
//     })
//   })
//
//   return agreementsGrandTotal
// }

export { LendingMap as default, getColors }
